import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PATH } from '@/router/PATH';
import { useResetOnKeyChange } from '@/utils';
import type { ErrorResponse } from '@/api/handlers/responses';
import { useProductListQuery } from '@/api/queries';
import {
    DEFAULT_PRODUCT_LIST_FILTERS,
    clearProductFilterGroup,
    getActiveProductFilterGroups,
    hasActiveProductListFilters,
    readProductListFilters,
    toProductExportQuery,
    toProductListQuery,
    writeProductListFilters,
    type ProductFilterGroup,
    type ProductListFilters,
} from './components/productListFilters';
import {
    buildAppliedFilterTags,
    type AppliedFilterTag,
    type NamedOption,
} from './components/productFilters/productFiltersMeta';
import { buildCategoryTree } from './components/productFilters/categoryPickerMeta';
import type {
    ProductBranchScope,
    StockFiguresScope,
} from './components/productFilters/branchScopeMeta';
import { useProductFilterOptions } from './useProductFilterOptions';
import {
    useProductFiltersDrawer,
    type ProductFiltersDrawerState,
} from './useProductFiltersDrawer';
import {
    useProductOriginState,
    type ProductOriginState,
} from './components/productOrigin';
import type { ProductExportScope } from './components/productExport/useProductExport';
import type { FilterOptionsState } from './components/productFilters/useFilterOptionsState';
import {
    getPaginationWindow,
    type CategoryFilterOption,
    type Product,
} from './components/productsMeta';

export type UseProductsPageResult = {
    data: Product[] | null;
    error: ErrorResponse | null;
    isLoading: boolean;
    isFetching: boolean;
    onRetry: () => void;
    // Search (inline) and filters (drawer)
    search: string;
    onSearchChange: (value: string) => void;
    /** The category tree, flattened in tree order with each one's depth. */
    categoryOptions: FilterOptionsState<CategoryFilterOption>;
    brandOptions: FilterOptionsState<NamedOption>;
    /** The drawer's branch field: a picker, a read-only line, or nothing. */
    branchScope: ProductBranchScope;
    /** Whose stock the figures count, as the Cost header's tooltip says. */
    stockFigures: StockFiguresScope;
    /**
     * The branch the list asks for, which each product link carries to its
     * details page (bos-app#119); `null` for the caller's own scope.
     */
    branchId: string | null;
    /**
     * What each product link records in router state: this entry as the
     * product's origin, which a Delete returns to (bos-app#137).
     */
    productLinkState: ProductOriginState | undefined;
    filtersDrawer: ProductFiltersDrawerState;
    /**
     * What Export Excel writes out (bos-app#120): the applied search, filters
     * and branch, across all pages. The export section owns the run.
     */
    exportScope: ProductExportScope;
    /** Applied filter groups, the Filters button's badge. The search isn't one. */
    filterCount: number;
    /** One per applied group, for the tags row. */
    appliedFilterTags: AppliedFilterTag[];
    /** A tag's ×: clears that group at once. */
    onRemoveFilter: (group: ProductFilterGroup) => void;
    /** Whether anything narrows the list, the search included. */
    hasActiveFilters: boolean;
    /**
     * The tags row's Clear all and the empty state's Clear filters: the search
     * and every filter, back to the whole catalog.
     */
    onResetFilters: () => void;
    /** Whether the list shows drafts only (bos-app#226). */
    isDraftsOnly: boolean;
    /**
     * The drafts notice's Show drafts: the Drafts filter added to the list as
     * it is, keeping the search, every other filter, the branch and the page
     * size, on page 1.
     */
    onShowDrafts: () => void;
    /** Opens the create-product route — the toolbar's primary CTA. */
    onCreate: () => void;
    // Pagination
    page: number;
    pageSize: number;
    totalCount: number;
    /**
     * The whole catalogue the result is out of — "33 of 1,488 products" —
     * while a search or filter narrows the list (BOS-201). `null` when
     * nothing narrows it (Show archived alone widens it), before the first
     * page, and from a server that doesn't report it.
     */
    catalogCount: number | null;
    totalPages: number;
    pageWindow: (number | 'ellipsis')[];
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
};

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Drives the Products table. Fetches the matching `/products` page
 * (server-side paging, debounced search) and feeds the category-tree +
 * brand-list queries that populate the filters drawer's pickers, each with
 * how its read went.
 *
 * The applied filters, page and page size live in the page address, not in
 * state (BOS-196): refresh, back/forward and a shared link all reopen the same
 * list. `productListFilters` reads and writes it; this hook decides when. A
 * deliberate change (Apply, a tag's ×, a page, Reset) adds a history entry and
 * returns to page 1 unless it *is* the page. The search box replaces the
 * current entry instead, so typing doesn't leave one step per keystroke.
 *
 * Every filter but the search is edited in the drawer (BOS-199), which holds
 * its changes until Apply; see `ProductFiltersDrawerState`.
 *
 * Exposes the standard data contract (`data` / `error` / `isLoading` /
 * `onRetry`) so the container reads like the other self-served tables.
 * `keepPreviousData` in the query keeps the current rows visible while the
 * next page loads (`isFetching` flags that).
 */
export const useProductsPage = (): UseProductsPageResult => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // The address's filters, with its branch as this caller may ask for it
    // (bos-app#118): the session's role and the branch list decide. Everything
    // below, writes included, works from these, so a branch the caller can't
    // ask for leaves the address on the next change.
    const addressFilters = React.useMemo(
        () => readProductListFilters(searchParams),
        [searchParams],
    );
    const {
        addressBranch,
        branches,
        branchScope,
        stockFigures,
        categoryOptions,
        brandOptions,
    } = useProductFilterOptions(addressFilters.branchId);
    const filters = React.useMemo<ProductListFilters>(
        () => ({ ...addressFilters, branchId: addressBranch.branchId }),
        [addressBranch.branchId, addressFilters],
    );

    /**
     * Writes `patch` over the applied filters. Anything but a page change lands
     * on page 1. Writing filters the address already holds is a no-op, however
     * the address spells them, so picking the same value again doesn't add a
     * history entry.
     */
    const applyFilters = React.useCallback(
        (
            patch: Partial<ProductListFilters>,
            { replace = false }: { replace?: boolean } = {},
        ) => {
            const next = writeProductListFilters(searchParams, {
                ...filters,
                page: 1,
                ...patch,
            });
            const current = writeProductListFilters(searchParams, filters);
            if (next.toString() !== current.toString()) {
                setSearchParams(next, { replace });
            }
        },
        [filters, searchParams, setSearchParams],
    );

    // The box is local so typing stays instant; the address only takes the
    // search once it settles. When the address moves on its own (back/forward,
    // a pasted link), the box follows it, unless it already reads the same
    // search give or take the spaces the address trims off.
    //
    // The hook's own write is not the address moving on its own. The router
    // applies it as a low-priority update, so a key typed meanwhile is already
    // in the box when it lands; following it would drop that key.
    const [searchInput, setSearchInput] = React.useState(filters.search);
    const writtenSearch = React.useRef<string | null>(null);
    useResetOnKeyChange(filters.search, () => {
        const isOwnWrite = filters.search === writtenSearch.current;
        writtenSearch.current = null;
        if (!isOwnWrite && searchInput.trim() !== filters.search) {
            setSearchInput(filters.search);
        }
    });

    // Debounce the search box so each keystroke doesn't fire a request. It
    // only writes when the box differs from the address, so opening a link
    // that carries a search and a page keeps the page.
    React.useEffect(() => {
        const settled = searchInput.trim();
        if (settled === filters.search) {
            return undefined;
        }
        const id = setTimeout(() => {
            writtenSearch.current = settled;
            applyFilters({ search: settled }, { replace: true });
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [applyFilters, filters.search, searchInput]);

    const query = React.useMemo(() => toProductListQuery(filters), [filters]);

    // Waits while the branch list checks the address's branch, rather than
    // showing every branch's figures under that branch's name.
    const listQuery = useProductListQuery(query, !addressBranch.isPending);

    const envelope = listQuery.data;
    const successData = envelope && envelope.success ? envelope.response : null;

    const data: Product[] | null = successData ? successData.items : null;
    const error: ErrorResponse | null =
        envelope && !envelope.success ? envelope.error : null;

    const totalCount = successData?.totalCount ?? 0;
    const totalPages = successData?.totalPages ?? 0;
    // Against the applied filters, not the box: the count it sits beside
    // answers for what the last request asked. Show archived widens the list
    // rather than narrowing it (the catalogue counts archived products while
    // it's on), so alone it would read "1,500 of 1,500".
    const catalogCount = hasActiveProductListFilters({
        ...filters,
        includeArchived: false,
    })
        ? (successData?.catalogCount ?? null)
        : null;

    const pageWindow = React.useMemo(
        () => getPaginationWindow(totalPages, filters.page),
        [filters.page, totalPages],
    );

    const categoryTree = React.useMemo(
        () => buildCategoryTree(categoryOptions.items),
        [categoryOptions.items],
    );
    const brandItems = brandOptions.items;

    const appliedFilterTags = React.useMemo(
        () =>
            buildAppliedFilterTags(filters, categoryTree, brandItems, branches),
        [branches, brandItems, categoryTree, filters],
    );

    const filterCount = getActiveProductFilterGroups(filters).length;

    // The applied search and filters, not the box's: the file holds what the
    // list shows. Rows kept from another filter set while the new one loads
    // don't count: the export would write the new set.
    const exportTotalCount =
        successData && !listQuery.isPlaceholderData
            ? successData.totalCount
            : null;
    const exportScope = React.useMemo<ProductExportScope>(
        () => ({
            query: toProductExportQuery(filters),
            search: filters.search,
            stockFigures,
            appliedFilterTags,
            totalCount: exportTotalCount,
        }),
        [appliedFilterTags, exportTotalCount, filters, stockFigures],
    );

    // Counts what's typed, not only what has settled, so the empty state
    // offers Clear filters at once.
    const hasActiveFilters = hasActiveProductListFilters({
        ...filters,
        search: searchInput.trim(),
    });

    const onSearchChange = React.useCallback((value: string) => {
        setSearchInput(value);
    }, []);

    const filtersDrawer = useProductFiltersDrawer(filters, applyFilters);

    const onRemoveFilter = React.useCallback(
        (group: ProductFilterGroup) =>
            applyFilters(clearProductFilterGroup(group)),
        [applyFilters],
    );

    // Clears the search and every filter, Show archived included. The page
    // size stays: it's how the user reads the list, not what's in it.
    const onResetFilters = React.useCallback(() => {
        setSearchInput('');
        applyFilters({
            ...DEFAULT_PRODUCT_LIST_FILTERS,
            pageSize: filters.pageSize,
        });
    }, [applyFilters, filters.pageSize]);

    // Adds the Drafts filter to what the list already shows (review of
    // bos-app#229, finding 25): the search, the other filters, the branch and
    // the page size stay; the list goes back to page 1.
    const onShowDrafts = React.useCallback(() => {
        applyFilters({ draft: true });
    }, [applyFilters]);

    const onPageChange = React.useCallback(
        (next: number) => {
            const clamped = Math.min(
                Math.max(next, 1),
                Math.max(totalPages, 1),
            );
            applyFilters({ page: clamped });
        },
        [applyFilters, totalPages],
    );

    const onPageSizeChange = React.useCallback(
        (size: number) => applyFilters({ pageSize: size }),
        [applyFilters],
    );

    const onRetry = React.useCallback(() => {
        listQuery.refetch();
    }, [listQuery]);

    // The list is the origin of a product opened from it (bos-app#137), by
    // its link or through New product, so a Delete comes back to this entry.
    const productLinkState = useProductOriginState();
    const onCreate = React.useCallback(
        () => navigate(PATH.PRODUCTS.NEW, { state: productLinkState }),
        [navigate, productLinkState],
    );

    return {
        data,
        error,
        isLoading: listQuery.isLoading,
        isFetching: listQuery.isFetching,
        onRetry,
        search: searchInput,
        onSearchChange,
        categoryOptions,
        brandOptions,
        branchScope,
        stockFigures,
        branchId: filters.branchId,
        productLinkState,
        filtersDrawer,
        exportScope,
        filterCount,
        appliedFilterTags,
        onRemoveFilter,
        hasActiveFilters,
        onResetFilters,
        isDraftsOnly: filters.draft,
        onShowDrafts,
        onCreate,
        page: filters.page,
        pageSize: filters.pageSize,
        totalCount,
        catalogCount,
        totalPages,
        pageWindow,
        onPageChange,
        onPageSizeChange,
    };
};
